/**
 * Hook to load and cache terrain icons from API Gateway
 * Similar to resource icons, but for terrain types on the game board
 */

import { useState, useEffect } from 'react';

// Icon cache configuration
const CACHE_PREFIX = 'rush-terrain-icon-';
const CACHE_VERSION = '8'; // Incremented to clear corrupted data URI cache
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Terrain type to icon filename mapping
const TERRAIN_ICON_MAP: Record<string, string> = {
  lunar: 'mare-8bit.png',
  highland: 'highland-8bit.png',
  crater: 'crater-8bit.png',
  mountain: 'mountain-8bit.png',
  regolith: 'regolith-8bit.png',
  base: 'base-8bit.png',
  landingPad: 'landing-pad-8bit.png', // Landing pad for start_game animation
  // Legacy support
  valley: 'regolith-8bit.png',
};

/**
 * Get cached terrain icon
 */
const getCachedIcon = (iconName: string): string | null => {
  try {
    const cacheKey = `${CACHE_PREFIX}${iconName}-v${CACHE_VERSION}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      // Also check for old versions and remove them
      ['1', '2', '3', '4'].forEach(oldVersion => {
        const oldCacheKey = `${CACHE_PREFIX}${iconName}-v${oldVersion}`;
        const oldCached = localStorage.getItem(oldCacheKey);
        if (oldCached) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`🗑️ Removing old cache version ${oldVersion} for ${iconName}`);
          }
          localStorage.removeItem(oldCacheKey);
        }
      });
      return null;
    }
    
    const { dataUri, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - timestamp > CACHE_EXPIRY_MS) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🗑️ Terrain cache expired for ${iconName}, will refetch`);
      }
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Using cached terrain icon: ${iconName} (v${CACHE_VERSION})`);
    }
    
    return dataUri;
  } catch (error) {
    console.error(`❌ Error reading terrain cache for ${iconName}:`, error);
    return null;
  }
};

/**
 * Cache terrain icon
 */
const cacheIcon = (iconName: string, dataUri: string): void => {
  try {
    const cacheKey = `${CACHE_PREFIX}${iconName}-v${CACHE_VERSION}`;
    const cacheData = {
      dataUri,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`💾 Cached terrain icon: ${iconName}`);
    }
  } catch (error) {
    console.warn(`⚠️ Failed to cache terrain icon ${iconName}:`, error);
  }
};

/**
 * Hook to load terrain icons with caching
 * Returns a map of terrain type -> data URI
 */
export function useTerrainIcons(): Record<string, string> {
  const [iconUrls, setIconUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadIcons = async () => {
      const terrainTypes = Object.keys(TERRAIN_ICON_MAP);
      const loadedIcons: Record<string, string> = {};
      
      for (const terrainType of terrainTypes) {
        const iconName = TERRAIN_ICON_MAP[terrainType];
        
        // Check cache first
        const cachedDataUri = getCachedIcon(iconName);
        
        if (cachedDataUri) {
          loadedIcons[terrainType] = cachedDataUri;
          continue; // Skip API call, use cached version
        }
        
        // Cache miss or expired - fetch from get_icon API (S3 via Lambda)
        try {
          const { apiClient } = await import('@/services/api');
          const dataUrl = await apiClient.getIcon(iconName);
          
          loadedIcons[terrainType] = dataUrl;
          
          // Cache the icon for future use
          cacheIcon(iconName, dataUrl);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Fetched and cached terrain icon from API: ${iconName}`);
          }
        } catch (error) {
          console.error(`❌ Error loading terrain icon ${iconName}:`, error);
        }
      }
      
      setIconUrls(loadedIcons);
      
      if (process.env.NODE_ENV === 'development') {
        const cachedCount = terrainTypes.filter(t => getCachedIcon(TERRAIN_ICON_MAP[t]) !== null).length;
        console.log(`🎨 Terrain Icons - Loaded ${Object.keys(loadedIcons).length} icons (${cachedCount} from cache)`);
        console.log(`📋 Terrain icon URLs:`, Object.keys(loadedIcons).map(t => `${t}: ${loadedIcons[t]?.substring(0, 50)}...`));
      }
    };
    
    loadIcons();
  }, []);

  return iconUrls;
}

